import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  accountGroups,
  type AccountGroup,
  type AccountGroupCreate,
} from "@/api/tradingAccounts";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Admin page for account_groups — the policy hub that decides pricing,
 * routing, margin and hedge behavior for every trading account assigned
 * to the group.
 */
export function AdminAccountGroupsPage() {
  const list = useQuery({ queryKey: ["account-groups"], queryFn: () => accountGroups.list() });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AccountGroup | null>(null);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Account groups</h1>
        <Button onClick={() => setOpen(true)}>New group</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : list.data?.length === 0 ? (
            <EmptyState
              title="No account groups defined"
              description="Account groups carry pricing markup, routing default, margin parameters, and hedging policy. Every trading account belongs to one group."
              action={<Button onClick={() => setOpen(true)}>Create first group</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Name</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Mode</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Routing</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Matcher</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Markup (bid/ask bps)</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">IM%</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Hedge</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((g) => (
                  <tr key={g.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-medium text-term-fg">{g.name}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={g.mode === "LIVE" ? "warn" : "neutral"}>{g.mode}</Badge>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-term-dim">{g.default_routing}</td>
                    <td className="px-3 py-1.5 font-mono text-term-dim">{g.b_book_matcher}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">
                      {Number(g.markup_bps_bid).toFixed(2)} / {Number(g.markup_bps_ask).toFixed(2)}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{g.im_pct}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={g.hedge_mode === "AUTO" ? "good" : g.hedge_mode === "MANUAL" ? "warn" : "neutral"}>
                        {g.hedge_mode}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex justify-end">
                        <Button variant="ghost" onClick={() => setEditing(g)}>
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <CreateGroupModal open={open} onClose={() => setOpen(false)} />
      <EditGroupModal group={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

const DEFAULTS: AccountGroupCreate = {
  name: "",
  mode: "PAPER",
  base_currency: "USD",
  default_routing: "MIXED",
  markup_bps_bid: "2",
  markup_bps_ask: "2",
  commission_per_lot: "0",
  swap_long_bps: "0",
  swap_short_bps: "0",
  im_pct: "1.0",
  mc_level_pct: "50",
  so_level_pct: "20",
  hedge_mode: "AUTO",
  hedge_threshold_lots: "1",
  b_book_pct: "50",
  b_book_matcher: "L1",
};

function CreateGroupModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<AccountGroupCreate>(DEFAULTS);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => accountGroups.create(form),
    onSuccess: () => {
      toast.push("Group created", "success");
      qc.invalidateQueries({ queryKey: ["account-groups"] });
      onClose();
      setForm(DEFAULTS);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  return (
    <Modal open={open} onClose={onClose} title="New account group" size="lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
        className="grid grid-cols-2 gap-3"
      >
        <div className="col-span-2">
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div>
          <Label>Mode</Label>
          <Select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as "PAPER" | "LIVE" })}>
            <option>PAPER</option>
            <option>LIVE</option>
          </Select>
        </div>
        <div>
          <Label>Default routing</Label>
          <Select
            value={form.default_routing}
            onChange={(e) => setForm({ ...form, default_routing: e.target.value as "A_BOOK" | "B_BOOK" | "MIXED" })}
          >
            <option>A_BOOK</option>
            <option>B_BOOK</option>
            <option>MIXED</option>
          </Select>
        </div>
        <div>
          <Label>Markup bid (bps)</Label>
          <Input value={String(form.markup_bps_bid)} onChange={(e) => setForm({ ...form, markup_bps_bid: e.target.value })} />
        </div>
        <div>
          <Label>Markup ask (bps)</Label>
          <Input value={String(form.markup_bps_ask)} onChange={(e) => setForm({ ...form, markup_bps_ask: e.target.value })} />
        </div>
        <div>
          <Label>Commission / lot</Label>
          <Input value={String(form.commission_per_lot)} onChange={(e) => setForm({ ...form, commission_per_lot: e.target.value })} />
        </div>
        <div>
          <Label>Swap long (bps)</Label>
          <Input value={String(form.swap_long_bps ?? "0")} onChange={(e) => setForm({ ...form, swap_long_bps: e.target.value })} />
        </div>
        <div>
          <Label>Swap short (bps)</Label>
          <Input value={String(form.swap_short_bps ?? "0")} onChange={(e) => setForm({ ...form, swap_short_bps: e.target.value })} />
        </div>
        <div>
          <Label>B-book split %</Label>
          <Input value={String(form.b_book_pct)} onChange={(e) => setForm({ ...form, b_book_pct: e.target.value })} />
        </div>
        <div>
          <Label>B-book matcher</Label>
          <Select
            value={form.b_book_matcher ?? "L1"}
            onChange={(e) => setForm({ ...form, b_book_matcher: e.target.value as "L1" | "L2" })}
          >
            <option value="L1">L1 — fill at quote</option>
            <option value="L2">L2 — order book</option>
          </Select>
        </div>
        <div>
          <Label>Initial margin %</Label>
          <Input value={String(form.im_pct)} onChange={(e) => setForm({ ...form, im_pct: e.target.value })} />
        </div>
        <div>
          <Label>Margin call level %</Label>
          <Input value={String(form.mc_level_pct)} onChange={(e) => setForm({ ...form, mc_level_pct: e.target.value })} />
        </div>
        <div>
          <Label>Stop-out level %</Label>
          <Input value={String(form.so_level_pct)} onChange={(e) => setForm({ ...form, so_level_pct: e.target.value })} />
        </div>
        <div>
          <Label>Hedge mode</Label>
          <Select
            value={form.hedge_mode}
            onChange={(e) => setForm({ ...form, hedge_mode: e.target.value as "AUTO" | "MANUAL" | "OFF" })}
          >
            <option>AUTO</option>
            <option>MANUAL</option>
            <option>OFF</option>
          </Select>
        </div>
        <div>
          <Label>Hedge threshold (lots)</Label>
          <Input
            value={String(form.hedge_threshold_lots)}
            onChange={(e) => setForm({ ...form, hedge_threshold_lots: e.target.value })}
          />
        </div>
        <div className="col-span-2">
          <FormError>{error}</FormError>
        </div>
        <div className="col-span-2 flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EditGroupModal({ group, onClose }: { group: AccountGroup | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<AccountGroup | null>(group);
  const [error, setError] = useState<string | null>(null);

  // sync when target changes
  if (group && form?.id !== group.id) setForm(group);

  const patch = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("no form");
      return accountGroups.patch(form.id, {
        markup_bps_bid: form.markup_bps_bid,
        markup_bps_ask: form.markup_bps_ask,
        commission_per_lot: form.commission_per_lot,
        swap_long_bps: form.swap_long_bps,
        swap_short_bps: form.swap_short_bps,
        im_pct: form.im_pct,
        mc_level_pct: form.mc_level_pct,
        so_level_pct: form.so_level_pct,
        hedge_mode: form.hedge_mode,
        hedge_threshold_lots: form.hedge_threshold_lots,
        b_book_pct: form.b_book_pct,
        b_book_matcher: form.b_book_matcher,
        default_routing: form.default_routing,
      });
    },
    onSuccess: () => {
      toast.push("Group saved", "success");
      qc.invalidateQueries({ queryKey: ["account-groups"] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  if (!group || !form) return null;

  function update<K extends keyof AccountGroup>(k: K, v: AccountGroup[K]) {
    setForm((cur) => (cur ? { ...cur, [k]: v } : cur));
  }

  return (
    <Modal open={true} onClose={onClose} title={`Edit ${group.name}`} size="lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          patch.mutate();
        }}
        className="grid grid-cols-2 gap-3"
      >
        <div>
          <Label>Default routing</Label>
          <Select value={form.default_routing} onChange={(e) => update("default_routing", e.target.value as AccountGroup["default_routing"])}>
            <option>A_BOOK</option>
            <option>B_BOOK</option>
            <option>MIXED</option>
          </Select>
        </div>
        <div>
          <Label>Hedge mode</Label>
          <Select value={form.hedge_mode} onChange={(e) => update("hedge_mode", e.target.value as AccountGroup["hedge_mode"])}>
            <option>AUTO</option>
            <option>MANUAL</option>
            <option>OFF</option>
          </Select>
        </div>
        <div>
          <Label>Markup bid (bps)</Label>
          <Input value={form.markup_bps_bid} onChange={(e) => update("markup_bps_bid", e.target.value)} />
        </div>
        <div>
          <Label>Markup ask (bps)</Label>
          <Input value={form.markup_bps_ask} onChange={(e) => update("markup_bps_ask", e.target.value)} />
        </div>
        <div>
          <Label>Commission / lot</Label>
          <Input value={form.commission_per_lot} onChange={(e) => update("commission_per_lot", e.target.value)} />
        </div>
        <div>
          <Label>Swap long (bps)</Label>
          <Input value={form.swap_long_bps} onChange={(e) => update("swap_long_bps", e.target.value)} />
        </div>
        <div>
          <Label>Swap short (bps)</Label>
          <Input value={form.swap_short_bps} onChange={(e) => update("swap_short_bps", e.target.value)} />
        </div>
        <div>
          <Label>B-book split %</Label>
          <Input value={form.b_book_pct} onChange={(e) => update("b_book_pct", e.target.value)} />
        </div>
        <div>
          <Label>B-book matcher</Label>
          <Select
            value={form.b_book_matcher}
            onChange={(e) => update("b_book_matcher", e.target.value as "L1" | "L2")}
          >
            <option value="L1">L1 — fill at quote</option>
            <option value="L2">L2 — order book</option>
          </Select>
        </div>
        <div>
          <Label>Initial margin %</Label>
          <Input value={form.im_pct} onChange={(e) => update("im_pct", e.target.value)} />
        </div>
        <div>
          <Label>Margin call %</Label>
          <Input value={form.mc_level_pct} onChange={(e) => update("mc_level_pct", e.target.value)} />
        </div>
        <div>
          <Label>Stop-out %</Label>
          <Input value={form.so_level_pct} onChange={(e) => update("so_level_pct", e.target.value)} />
        </div>
        <div>
          <Label>Hedge threshold (lots)</Label>
          <Input value={form.hedge_threshold_lots} onChange={(e) => update("hedge_threshold_lots", e.target.value)} />
        </div>
        <div className="col-span-2">
          <FormError>{error}</FormError>
        </div>
        <div className="col-span-2 flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={patch.isPending}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
